import type { RxJsonSchema } from 'rxdb'

/**
 * UserPreferences Document
 *
 * Per-(user, workspace) preferences. Exactly one row per workspace membership;
 * the row is server-seeded on first pull and is permanent (no soft-delete).
 *
 * Privacy: this collection is filtered to the authenticated user on the server's
 * pull endpoint, and the WebSocket stream filter in `userPreferencesReplication`
 * drops broadcasts that target other users. Local RxDB therefore only ever holds
 * the current user's row for the active workspace.
 */
export interface UserPreferencesDocument {
  id: string
  envId: string
  userId: string

  language: string
  dateFormat: string
  timeFormat: string
  weekStartsOn: number
  theme: string
  defaultView: string
  workingHoursJson?: string | null
  notificationsJson?: string | null
  timeZone: string

  isDeleted: boolean

  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null
  updatedAt?: number
}

export const userPreferencesSchema: RxJsonSchema<UserPreferencesDocument> = {
  // v1: bumped notificationsJson maxLength 5000 → 10000 to fit the expanded
  // per-event preference matrix (~9 events × 4 channels) plus DismissedNotices.
  // No data shape change; the migration is a passthrough.
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 36 },
    envId: { type: 'string', maxLength: 36 },
    userId: { type: 'string', maxLength: 36 },
    language: { type: 'string', maxLength: 10 },
    dateFormat: { type: 'string', maxLength: 20 },
    timeFormat: { type: 'string', maxLength: 5 },
    weekStartsOn: { type: 'number', minimum: 0, maximum: 6, multipleOf: 1 },
    theme: { type: 'string', maxLength: 20 },
    defaultView: { type: 'string', maxLength: 50 },
    workingHoursJson: { type: ['string', 'null'], maxLength: 1000 },
    notificationsJson: { type: ['string', 'null'], maxLength: 10000 },
    timeZone: { type: 'string', maxLength: 100 },
    isDeleted: { type: 'boolean', default: false },
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: { type: 'string', maxLength: 36 },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: { type: 'string', maxLength: 36 },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: { type: ['string', 'null'], maxLength: 36 },
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'userId',
    'language',
    'dateFormat',
    'timeFormat',
    'weekStartsOn',
    'theme',
    'defaultView',
    'timeZone',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [['envId', 'userId'], 'updatedOn'],
}

export const userPreferencesMigrationStrategies = {
  // v0 -> v1: bumped notificationsJson maxLength only. No data transform required.
  1: (oldDoc: UserPreferencesDocument) => oldDoc,
}
